import {Component, Input, OnInit} from '@angular/core';
import {DataTableComponent} from '../DataTableComponent';
import {SelectOption} from '../../models/select-option';
import {InvoiceService} from '../../services/invoice.service';
import {ConfirmationService} from '../../services/confirmation.service';
import {AuthService} from '../../services/auth.service';
import {NbToastrService} from '@nebular/theme';
import {ActivatedRoute, Router} from '@angular/router';
import {Invoice} from '../../models/invoice';
import {takeUntil} from 'rxjs/operators';
import {DateUtils} from '../../utils/date.utils';
import {formatDate} from '@angular/common';
import {CSVUtils} from '../../utils/csv.utils';

@Component({
  selector: 'app-table-invoice',
  templateUrl: './table-invoice.component.html',
  styleUrls: ['./table-invoice.component.scss']
})
export class TableInvoiceComponent extends DataTableComponent implements OnInit {

    @Input() operatorId: string = null;
    @Input() allowDownloadCSV = false;

    displayedColumns = ['operator', 'serialNumber', 'status', 'amount', 'drivers', 'createdTs', 'actions'];

    filterStatuses: SelectOption<any>[] = [
        {label: 'Noi', value: 'new'},
        {label: 'Platite', value: 'paid'},
        {label: 'Anulate', value: 'canceled'},
    ];
    monthOptions: { label: string, value: number }[] = [];
    selectedMonthForDownloadCSV: number;

    isLoading = false;
    isLoadingCSV = false;
    error: any = null;

    constructor(
        protected invoiceService: InvoiceService,
        private confirmationService: ConfirmationService,
        public authService: AuthService,
        toastr: NbToastrService,
        router: Router,
        route: ActivatedRoute
    ) {
        super(toastr, router, route, (params) => invoiceService.getAll(params, this.operatorId));
    }

    ngOnInit() {
        super.ngOnInit();

        this.invoiceService.dataChanged$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.handleParamsChanged();
            });
        this.populateMonthOptions();
    }

    populateMonthOptions() {
        if (!this.allowDownloadCSV) {
            return;
        }
        const currentMonth = (new Date()).getMonth() + 1;
        this.monthOptions = DateUtils.getLastMonths(6, currentMonth);
        this.selectedMonthForDownloadCSV = currentMonth;
    }

    selectedMonthChanged(month: number) {
        this.selectedMonthForDownloadCSV = month;
    }

    async exportInvoicesAsCSV() {
        try {
            this.isLoadingCSV = true;
            this.error = null;
            const invoicesByMonth = await this.invoiceService.getInvoicesByMonth(this.selectedMonthForDownloadCSV);
            this.exportCSV(invoicesByMonth, `facturi-luna-${DateUtils.getMonthByNumber(this.selectedMonthForDownloadCSV)}.csv`);
        } catch (e) {
            this.error = e.error.error.msg;
            await this.toastr.show(this.error, 'Eroare', {status: 'danger'});
        } finally {
            this.isLoadingCSV = false;
        }
    }

    exportCSV(data: Invoice[], file: string = 'invoices.csv', columnDelimiter: string = ',', lineDelimiter: string = '\n') {
        const keys = ['Nr. Factura', 'Data Factura', 'Nume companie', 'Cod Unic de Inregistrare Companie',
            'Valoare fara TVA', 'TVA', 'Valoare cu TVA'];

        let result = keys.join(columnDelimiter) + lineDelimiter;
        result += data.map(invoice => [
            `${invoice.serial}-${invoice.number}`,
            formatDate(invoice.createdTs, 'dd.MM.yyyy', 'en-US'),
            invoice.operator.company.name,
            invoice.operator.company.code,
            (invoice.amount / 100).toString(),
            '0',
            (invoice.amount / 100).toString(),
        ].map((item: string) => CSVUtils.escapeCsvItem(item))).map(item => item.join(columnDelimiter))
            .join(lineDelimiter);

        const csv = 'data:text/csv;charset=utf-8,' + result;
        const uri = encodeURI(csv);
        this.downloadFile(uri, file);
    }

    downloadFile(dataUri: string, filename: string) {
        const link = document.createElement('a');
        link.setAttribute('href', dataUri);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    async downloadInvoice(invoice: Invoice) {
        try {
            const {url} = await this.invoiceService.getDownloadUrl(invoice._id);
            window.open(url, '_blank');
        } catch (e) {
            this.error = e.error?.error?.msg || 'Eroare la descărcarea facturii';
            await this.toastr.show(this.error, 'Eroare', {status: 'danger'});
        }
    }

    markAsPaid(invoice: Invoice) {
        if (invoice.status === 'new') {
            this.confirmationService.confirm({
                onSuccess: async () => {
                    this.isLoading = true;
                    await this.invoiceService.markAsPaid(invoice._id);
                    invoice.status = 'paid';
                    this.isLoading = false;
                    await this.toastr.show('Factura a fost marcată ca plătită', 'Factura', {status: 'success'});
                },
                title: 'Factura',
                message: `Dorești să marchezi aceasta factură în valoare de ${invoice.amount / 100} Lei ca plătită?`
            });
        }
    }
}
